import React, { ComponentPropsWithRef, useRef, useState } from 'react';

import { IconSearch, IconX } from '@tabler/icons-react';
import { useClickOutside } from 'hooks/useClickOutside';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { TextField, TextFieldType, Typography } from '@cfa/react-components';
import { Surface, SurfaceProps } from '@cfa/react-core';

import { cn } from '@/utils/tailwind';

import SearchbarSuggestionsDropdown from './SearchbarSuggestionsDropdown';

import { searchAtom, useSearchDispatch } from '@/atoms/search';

interface SearchbarProps extends Pick<
  ComponentPropsWithRef<'div'>,
  'ref' | 'className'
> {
  searchValue?: string;
  showSearchTitle?: boolean;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<TextFieldType>) => void;
  onSubmit?: (suggestion?: string) => void;
  onClear: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  showRecentSearchDropdown?: boolean;
  elevation?: SurfaceProps['elevation'];
}

const Searchbar: React.FC<SearchbarProps> = ({
  searchValue,
  showSearchTitle,
  placeholder,
  onChange,
  onSubmit,
  onClear,
  onFocus,
  onBlur,
  showRecentSearchDropdown = false,
  elevation = 0,
  className,
  ref,
  ...props
}) => {
  const { t } = useTranslation();
  const { terms: pastSearchTerms } = useAtomValue(searchAtom);
  const searchDispatch = useSearchDispatch();
  const internalRef = useRef<HTMLDivElement>(null);
  const [suggestionsIsOpen, setSuggestionsIsOpen] = useState(false);

  const mergedRef = (node: HTMLDivElement) => {
    internalRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const onFocusSearchInput = () => {
    if (showRecentSearchDropdown) setSuggestionsIsOpen(true);
    onFocus?.();
  };

  const onBlurSearchInput = () => {
    if (showRecentSearchDropdown) setSuggestionsIsOpen(false);
    onBlur?.();
  };

  const onSubmitSearch: React.SubmitEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    if (!searchValue?.trim()) return;
    if (showRecentSearchDropdown) {
      searchDispatch.saveTerm(searchValue);
      setSuggestionsIsOpen(false);
    }
    onSubmit?.();
  };

  const onClickSuggestion = (suggestion: string) => {
    if (showRecentSearchDropdown) {
      searchDispatch.saveTerm(suggestion);
      setSuggestionsIsOpen(false);
    }
    onSubmit?.(suggestion);
  };

  const onClickRemoveSuggestion = (suggestion: string) => {
    if (showRecentSearchDropdown) {
      searchDispatch.removeTerm(suggestion);
    }
  };

  useClickOutside(internalRef, onBlurSearchInput);

  return (
    <div ref={mergedRef} className="w-full">
      <Surface
        {...props}
        elevation={elevation}
        className={cn(
          `relative flex w-full flex-col gap-2 overflow-visible`,
          className,
        )}
      >
        {showSearchTitle && (
          <Typography className="mb-2 font-bold!">
            {t('Generic.search')}
          </Typography>
        )}
        <form
          onSubmit={onSubmitSearch}
          className={`${showSearchTitle ? 'mb-2' : ''} relative`}
        >
          <TextField
            className="[&_.cfa-helper-text-wrapper]:pt-0! [&_input]:pr-[60px]"
            data-testid="Search"
            fullWidth
            onChange={onChange}
            onClick={onFocusSearchInput}
            placeholder={placeholder ?? t('Search.searchForResources')}
            textAlign="start"
            value={searchValue}
          />
          <button
            type="submit"
            className="absolute top-[14px] right-[14px] border-none bg-none p-0 leading-none"
            data-testid="SearchMagnifyButton"
          >
            <IconSearch
              className={`size-[18px] ${
                searchValue ? 'text-gray-600' : 'text-gray-300'
              }`}
            />
          </button>
          {searchValue && (
            <IconX
              className="absolute top-[14px] right-[40px] size-[18px] cursor-pointer"
              onClick={onClear}
              data-testid="ClearSearchButton"
            />
          )}
        </form>
        {suggestionsIsOpen && (
          <SearchbarSuggestionsDropdown
            onRemoveSuggestion={onClickRemoveSuggestion}
            onSetSuggestion={onClickSuggestion}
            suggestions={pastSearchTerms}
          />
        )}
      </Surface>
    </div>
  );
};

export default Searchbar;
